import { useCallback, useEffect, useRef, useState } from 'react'
import { AdvancedApiError, advancedGet, advancedPut } from './client'

export const CARE_PLAN_SAVE_DELAY_MS = 600

type StoredCarePlan<T> = { document: T; version: number }
type CarePlanResponse<T> = { carePlan: StoredCarePlan<T> | null }
// `exists` false: nothing is stored yet and nothing has been started here.
type Loaded<T> = { path: string; value: T; version: number | null; exists: boolean }

export const carePlanPath = (uid: string, role: string) =>
  `/patients/${encodeURIComponent(uid)}/care-plans/${encodeURIComponent(role)}`

// Same-tab sync — tabs stay mounted, so one plan can be open in several panels at once.
type Listener = (loaded: Loaded<unknown>, source: symbol) => void
const listenersByPath = new Map<string, Set<Listener>>()

const publish = (loaded: Loaded<unknown>, source: symbol) =>
  listenersByPath.get(loaded.path)?.forEach((listener) => listener(loaded, source))

const subscribe = (path: string, listener: Listener) => {
  if (!listenersByPath.has(path)) {
    listenersByPath.set(path, new Set())
  }
  listenersByPath.get(path)?.add(listener)

  return () => {
    listenersByPath.get(path)?.delete(listener)
  }
}

const OUTSIDE = Symbol('outside')

const planStatus = (loadError: unknown, ready: boolean): 'loading' | 'ready' | 'error' => {
  if (loadError) {
    return 'error'
  }

  return ready ? 'ready' : 'loading'
}

// Creates the plan only if none is stored; never overwrites one somebody may have edited.
export const createCarePlanIfMissing = async <T>(uid: string, role: string, document: T): Promise<boolean> => {
  const path = carePlanPath(uid, role)
  try {
    const { carePlan } = await advancedPut<{ carePlan: StoredCarePlan<T> }>(path, { document, version: null })
    publish({ path, value: carePlan.document, version: carePlan.version, exists: true }, OUTSIDE)
    return true
  } catch (error) {
    if (error instanceof AdvancedApiError && error.status === 409) {
      return false
    }
    throw error
  }
}

// The care team's shared plan (ADR-095).
export const useSharedCarePlan = <T extends object>(
  uid: string,
  role: string,
  fallback: T,
  { writable, enabled = true }: { writable: boolean; enabled?: boolean }
) => {
  const path = carePlanPath(uid, role)
  const [loaded, setLoaded] = useState<Loaded<T> | null>(null)
  const [loadError, setLoadError] = useState<unknown>(null)
  const [saveFailed, setSaveFailed] = useState(false)
  const [conflicted, setConflicted] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const [pendingSave, setPendingSave] = useState(0)
  const current = useRef<Loaded<T> | null>(null)
  const dirty = useRef(false)
  const inFlight = useRef(false)
  const self = useRef(Symbol('useSharedCarePlan'))
  const options = useRef({ writable, fallback })
  options.current = { writable, fallback }

  const commit = useCallback((next: Loaded<T>, broadcast = true) => {
    current.current = next
    setLoaded(next)
    if (broadcast) {
      publish(next, self.current)
    }
  }, [])

  const flush = useCallback(async () => {
    const snapshot = current.current
    if (!snapshot || !dirty.current || inFlight.current) {
      return
    }
    dirty.current = false
    inFlight.current = true
    try {
      const { carePlan } = await advancedPut<{ carePlan: StoredCarePlan<T> }>(snapshot.path, {
        document: snapshot.value,
        version: snapshot.version,
      })
      if (current.current?.path === snapshot.path) {
        commit({ ...current.current, version: carePlan.version, exists: true })
      }
      setSaveFailed(false)
    } catch (error) {
      if (error instanceof AdvancedApiError && error.status === 409) {
        // Someone else saved first: their plan wins and this browser's unsaved change is dropped.
        const { carePlan } = await advancedGet<CarePlanResponse<T>>(snapshot.path).catch(() => ({ carePlan: null }))
        if (carePlan && current.current?.path === snapshot.path) {
          dirty.current = false
          commit({ path: snapshot.path, value: carePlan.document, version: carePlan.version, exists: true })
          setConflicted(true)
        }
      } else if (current.current?.path === snapshot.path) {
        dirty.current = true
        setSaveFailed(true)
        return
      }
    } finally {
      inFlight.current = false
    }
    if (dirty.current) {
      setPendingSave((count) => count + 1)
    }
  }, [commit])

  useEffect(
    () =>
      subscribe(path, (next, source) => {
        if (source !== self.current) {
          commit(next as Loaded<T>, false)
        }
      }),
    [path, commit]
  )

  useEffect(() => {
    const controller = new AbortController()
    current.current = null
    dirty.current = false
    setLoaded(null)
    setLoadError(null)
    setConflicted(false)
    setSaveFailed(false)
    if (!enabled) {
      return undefined
    }

    advancedGet<CarePlanResponse<T>>(path, controller.signal)
      .then(({ carePlan }) => {
        if (controller.signal.aborted) {
          return
        }
        commit(
          {
            path,
            value: carePlan?.document ?? options.current.fallback,
            version: carePlan?.version ?? null,
            exists: Boolean(carePlan),
          },
          false
        )
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          setLoadError(error)
        }
      })

    return () => {
      controller.abort()
      flush().catch(() => undefined)
    }
  }, [path, attempt, enabled, flush, commit])

  useEffect(() => {
    if (pendingSave === 0) {
      return undefined
    }
    const timer = setTimeout(flush, CARE_PLAN_SAVE_DELAY_MS)

    return () => clearTimeout(timer)
  }, [pendingSave, flush])

  // `save: false` keeps a view-only change, like an expanded row, in this browser until the next real save.
  const set = useCallback(
    (next: T | ((prev: T) => T), { save = true }: { save?: boolean } = {}) => {
      const base = current.current
      if (!base) {
        return
      }
      const value = typeof next === 'function' ? (next as (prev: T) => T)(base.value) : next
      if (value === base.value) {
        return
      }
      commit({ ...base, value, exists: true })
      if (options.current.writable && save) {
        dirty.current = true
        setPendingSave((count) => count + 1)
      }
    },
    [commit]
  )

  const retry = useCallback(() => setAttempt((count) => count + 1), [])
  const dismissConflict = useCallback(() => setConflicted(false), [])
  const retrySave = useCallback(() => setPendingSave((count) => count + 1), [])
  const ready = loaded?.path === path ? loaded : null

  return {
    value: ready?.value ?? fallback,
    exists: Boolean(ready?.exists),
    set,
    status: planStatus(loadError, ready !== null),
    retry,
    saveFailed,
    retrySave,
    conflicted,
    dismissConflict,
  }
}
